import React, { FC } from "react";
import { Modal, Card, List, Avatar, Badge, Button } from "antd";
import styled from "styled-components";
import _ from "lodash";
import { CatalogItem } from "../../queries/auto-generated/schemas";
import logo from "../../logo.svg";
import SelectionForm from "./selection-form";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../ducks";
import catalog from "../../ducks/catalog";
import { calculatePrice, prettyMoney } from "../../helpers";
import { CloseOutlined, EditOutlined } from "@ant-design/icons";
import cart from "../../ducks/cart";
import { BuiltItem } from "../../types";
import { useItemAvailability } from "../../hooks/useItemAvailability";

const { Meta } = Card;

export const cardContentPadding = "24px";

export const ItemCard: FC<{
  data: CatalogItem;
  showDetails?: boolean;
}> = ({ data, showDetails }) => {
  const dispatch = useDispatch();

  const imageUrl = data?.meta?.image?.url ?? logo;

  const width = showDetails ? "100%" : 480;
  const height = showDetails ? 400 : 320;
  const margin = showDetails ? 0 : "0.75em";

  return (
    <>
      <StyledCard
        bordered={!showDetails}
        hoverable
        style={{ width, margin }}
        bodyStyle={{ padding: 0 }}
        cover={
          <Image
            alt={data?.name ?? "food item"}
            src={imageUrl}
            style={{ height }}
            inModal={showDetails}
          />
        }
        onClick={() => {
          if (!showDetails) {
            dispatch(
              catalog.actions.initCurrentItemSelectionChoices({
                itemId: data.id,
              })
            );
          }
        }}
      >
        <Meta
          title={data?.name}
          description={data?.description}
          style={{
            padding: cardContentPadding,
          }}
        />
        {showDetails && <SelectionForm catalogItem={data} />}
      </StyledCard>
    </>
  );
};

const Image = styled.img<{ inModal?: boolean }>`
  object-fit: ${(p) => (p.inModal ? "cover" : "contain")};
`;

export const ItemModal: FC<{
  data: CatalogItem;
  visible: boolean;
}> = ({ data, visible }) => {
  const dispatch = useDispatch();
  const lineItemIndex = useSelector(
    (state: RootState) => state.cart.currentEditedLineItemIndex
  );

  const handleCancel = () => {
    dispatch(catalog.actions.cancelSelection());
    if (lineItemIndex !== undefined) {
      dispatch(cart.actions.cancelEditLineItem());
    }
  };

  return visible ? (
    <StyledModal
      visible={visible}
      footer={null}
      centered={true}
      onCancel={handleCancel}
      bodyStyle={{ padding: 0, width: "100%" }}
      width={1000}
    >
      <ItemCard data={data} showDetails={true} />
    </StyledModal>
  ) : null;
};

export const LineItemDetails: FC<{
  id: string;
  builtItem: BuiltItem;
  lineItemIndex?: number;
}> = ({ id, builtItem, lineItemIndex }) => {
  const dispatch = useDispatch();

  const catalogItem = useSelector(
    (state: RootState) => state.catalog.data.items[id]
  );
  const modifiers = useSelector(
    (state: RootState) => state.catalog.data.modifiers
  );
  const variations = useSelector(
    (state: RootState) => state.catalog.data.variations
  );

  const canEdit = lineItemIndex !== undefined;

  if (!catalogItem || !builtItem || builtItem.item_id !== id) {
    console.error("missing line item");
    return <div></div>;
  }

  const chosenModifiers = _.filter(
    _.flatten(
      _.map(builtItem.modifications, (info) =>
        info.selected.map((square_id) =>
          _.find(modifiers, (mod) => mod.square_id === square_id)
        )
      )
    ),
    (mod) => mod !== undefined
  );

  const chosenVariation =
    (catalogItem.variations?.length ?? 0) > 1
      ? variations[builtItem.variation_id]
      : undefined;

  const price = calculatePrice(modifiers, variations, builtItem);
  const prettyPrice = prettyMoney(price);

  const editSelection = () => {
    if (canEdit) {
      dispatch(catalog.actions.loadCurrentItemSelectionChoices({ builtItem }));
      dispatch(
        cart.actions.editLineItem({ lineItemIndex: lineItemIndex as number })
      );
    }
  };

  const Choices: FC = () => {
    return (
      <StyledList>
        {chosenVariation && <li key="variation">{chosenVariation.name}</li>}
        {chosenModifiers.map((mod, index) => (
          <li key={index}>{mod?.name}</li>
        ))}
        {builtItem.note && <li key="notes">Note: {builtItem.note}</li>}
      </StyledList>
    );
  };

  return (
    <List.Item
      style={{ minHeight: "100px", alignItems: "flex-start" }}
      actions={
        canEdit && canEdit
          ? [
              <Button
                key="remove"
                size="small"
                shape="round"
                type="text"
                icon={<CloseOutlined />}
                onClick={() =>
                  dispatch(
                    cart.actions.remove({
                      lineItemIndex: lineItemIndex as number,
                    })
                  )
                }
              />,
            ]
          : []
      }
    >
      <MyMeta
        avatar={
          <div onClick={editSelection}>
            <Badge count={builtItem.quantity}>
              <Avatar src={catalogItem.meta?.image?.url ?? ""} />
            </Badge>
          </div>
        }
        title={
          <FullHeightButton
            type="link"
            style={{ paddingLeft: 0 }}
            onClick={editSelection}
          >
            <ItemTitle>
              {catalogItem.name}
              {canEdit && (
                <span style={{ paddingLeft: "1em" }}>
                  <EditOutlined />
                </span>
              )}
            </ItemTitle>
          </FullHeightButton>
        }
        description={<Choices />}
      />
      <div>{prettyPrice}</div>
    </List.Item>
  );
};

const ItemTitle = styled.span`
  white-space: break-spaces;
  text-align: left;
  color: black;
`;

const FullHeightButton = styled(Button)`
  height: auto;
`;

const StyledList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const MyMeta = styled(List.Item.Meta)``;

const ItemWithModal: FC<{ id: string }> = ({ id }) => {
  const catalogItem = useSelector(
    (state: RootState) => state.catalog.data.items[id]
  );
  const currentItemSelectionChoices = useSelector(
    (state: RootState) => state.catalog.currentItemSelectionChoices
  );

  const currentEditedLineItemIndex = useSelector(
    (state: RootState) => state.cart.currentEditedLineItemIndex
  );
  const { selected } = useSelector((state: RootState) => state.location);

  const { isAvailableAtLocation } = useItemAvailability();

  if (!isAvailableAtLocation(catalogItem, selected)) {
    return null;
  }

  const modalOpen =
    currentEditedLineItemIndex === undefined &&
    currentItemSelectionChoices?.item_id === id;

  return (
    <>
      <ItemCard data={catalogItem} />
      <ItemModal data={catalogItem} visible={modalOpen} />
    </>
  );
};

const StyledModal = styled(Modal)`
  max-width: 100%;
  .ant-modal-close {
    background-color: #0000006b;
    border-radius: 64px;
  }
`;

const StyledCard = styled(Card)``;

export default ItemWithModal;
